import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { assetUrl } from "@/lib/api";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const CART_KEY = "shopnow_cart";
const WISH_KEY = "shopnow_wishlist";
const CART_SID_KEY = "sn_cart_sid";

export function cartSessionId() {
  let s = localStorage.getItem(CART_SID_KEY);
  if (!s) { s = "cart-" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(CART_SID_KEY, s); }
  return s;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
  });
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem(WISH_KEY)) || []; } catch { return []; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem(WISH_KEY, JSON.stringify(wishlist)); }, [wishlist]);

  // Conversion analytics: track cart state (debounced) so the BI agent can spot abandonment.
  useEffect(() => {
    const t = setTimeout(() => {
      const count = items.reduce((s, i) => s + i.quantity, 0);
      const value = items.reduce((s, i) => s + i.price * i.quantity, 0);
      const lines = items.slice(0, 6).map((i) => ({ product_id: i.product_id, name: i.name, image: assetUrl(i.image), price: i.price, quantity: i.quantity }));
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/track`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ session_id: cartSessionId(), items: count, value, lines }),
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [items]);

  const addToCart = useCallback((product, qty = 1, openDrawer = true) => {
    const n = Math.trunc(Number(qty));
    if (!Number.isFinite(n) || n < 1) { toast.error("Select at least 1 to add to your cart"); return; }
    let capped = false;
    setItems((prev) => {
      const found = prev.find((i) => i.product_id === product.id);
      if (found) { const next = Math.min(99, found.quantity + n); capped = found.quantity + n > 99; return prev.map((i) => i.product_id === product.id ? { ...i, quantity: next } : i); }
      return [...prev, {
        product_id: product.id, name: product.name, price: product.price,
        quantity: Math.min(99, n), image: product.image, merchant_slug: product.merchant_slug,
        restaurant_slug: product.restaurant_slug, branch_id: product.branch_id, note: product.note, options: product.options, unit: product.unit, unit_weight_kg: product.unit_weight_kg ?? null,
      }];
    });
    toast.success(capped ? `${product.name}: maximum quantity of 99 items reached` : `${product.name} added to cart`);
    if (openDrawer) setDrawerOpen(true);
  }, []);

  const removeItem = useCallback((id) => setItems((p) => p.filter((i) => i.product_id !== id)), []);
  const setQty = useCallback((id, q) => { const n = Math.min(99, Math.max(0, Math.trunc(Number(q)) || 0)); setItems((p) => n === 0 ? p.filter((i) => i.product_id !== id) : p.map((i) => i.product_id === id ? { ...i, quantity: n } : i)); }, []);
  const clear = useCallback(() => setItems([]), []);

  const toggleWish = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.find((i) => i.id === product.id);
      if (exists) { toast("Removed from wishlist"); return prev.filter((i) => i.id !== product.id); }
      toast.success("Saved to wishlist");
      return [...prev, { id: product.id, name: product.name, price: product.price, image: product.image, merchant_slug: product.merchant_slug }];
    });
  }, []);

  const inWishlist = useCallback((id) => wishlist.some((i) => i.id === id), [wishlist]);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);

  const value = useMemo(() => ({
    items, count, subtotal, addToCart, removeItem, setQty, clear, clearCart: clear,
    drawerOpen, setDrawerOpen, wishlist, toggleWish, inWishlist,
  }), [items, count, subtotal, addToCart, removeItem, setQty, clear, drawerOpen, wishlist, toggleWish, inWishlist]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
